import { Fragment, type ReactNode } from 'react';
import { useResolveText } from '../../composition/RunnerTokenContext';

/**
 * Authored guide copy, with its emphasis.
 *
 * The Scenario Console writes its prose as small HTML fragments: `<b>` around
 * the point the seller is meant to land, `<i>` around the aside. That emphasis
 * is not decoration. It is what makes a paragraph skim-readable while someone
 * is talking, so it is carried through the importer and rendered here.
 *
 * Parsed into elements rather than passed to `dangerouslySetInnerHTML`. The
 * content is authored by Cisco rather than uploaded by a customer, so this is
 * not the same risk as a forwarded slide deck, but a renderer that can only
 * produce four tags cannot be made to produce a fifth by a bad import either.
 */

const TAG = /<(\/?)(b|strong|i|em)>/gi;

export function GuideText({ text }: { text: string }) {
  // Resolve `{{group.key}}` tokens before the emphasis parser runs, so authored guide copy shows the
  // delivery's real values rather than the raw token.
  const resolve = useResolveText();
  return <>{parse(resolve(text))}</>;
}

function parse(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  // One open tag at a time is all the source uses, so a stack is not needed:
  // the parser tracks which emphasis is currently open and closes it on the
  // matching tag or at the end of the string.
  let open: 'strong' | 'em' | null = null;
  let buffer = '';
  let at = 0;
  let key = 0;

  const flush = () => {
    if (!buffer) return;
    if (open === 'strong') out.push(<strong key={key++}>{buffer}</strong>);
    else if (open === 'em') out.push(<em key={key++}>{buffer}</em>);
    else out.push(<Fragment key={key++}>{buffer}</Fragment>);
    buffer = '';
  };

  TAG.lastIndex = 0;
  let match = TAG.exec(text);
  while (match) {
    buffer += text.slice(at, match.index);
    at = match.index + match[0].length;

    const closing = match[1] === '/';
    const kind = /^(b|strong)$/i.test(match[2]) ? 'strong' : 'em';
    if (closing) {
      if (open === kind) {
        flush();
        open = null;
      }
    } else if (open === null) {
      flush();
      open = kind;
    }

    match = TAG.exec(text);
  }

  buffer += text.slice(at);
  flush();
  return out;
}
